"use client";
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthenticationRequestFormSchema } from "@/validation/authSchemas";
import {
  Button,
  TextField,
  InputAdornment,
  InputBaseComponentProps,
} from "@mui/material";
import { PhoneNumberMask } from "./PhoneNumberMask";
import { AuthenticationRequestForm } from "@/types/auth";
import { clearedNumber } from "@/utils/phoneNumber";
import { useMnoContext } from "@/context/MnoContext";

interface LoginFormProps {
  onSubmit: (phoneNumber: string) => Promise<void>;
  isLoading: boolean;
  authStatusMessage?: string;
  approvalStatusMessage?: string;
  mobileNumber?: string;
}

const AuthForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading,
  authStatusMessage,
  approvalStatusMessage,
  mobileNumber,
}) => {
  const { config } = useMnoContext();
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<AuthenticationRequestForm>({
    defaultValues: {
      phoneNumber: mobileNumber ?? "",
    },
    resolver: zodResolver(AuthenticationRequestFormSchema),
  });

  const handleFormSubmit = async () => {
    const phoneNumber = clearedNumber(getValues().phoneNumber);
    await onSubmit(phoneNumber);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <TextField
        {...register("phoneNumber")}
        label="Mobile number"
        variant="outlined"
        fullWidth
        data-testid="phoneNumber"
        className="mb-4"
        error={!!errors.phoneNumber}
        helperText={errors.phoneNumber?.message}
        InputProps={{
          inputComponent:
            PhoneNumberMask as React.ComponentType<InputBaseComponentProps>,
          startAdornment: (
            <InputAdornment position="start">
              <span className="text-gray-500 font-medium">{config.countryCode}</span>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />

      <Button
        className="text-contrastText"
        color="primary"
        variant="contained"
        type="submit"
        fullWidth
        disabled={isLoading}
      >
        {isLoading ? "Logging in..." : "Verify now"}
      </Button>
      <p className="text-sm text-gray-500 text-center">
        We'll send you a notification to confirm it's you.
      </p>

      {authStatusMessage && (
        <p className="text-md text-error mt-2">{authStatusMessage}</p>
      )}
      {approvalStatusMessage && (
        <p className="text-md text-error mt-2">{approvalStatusMessage}</p>
      )}
    </form>
  );
};

export default AuthForm;
